import { cn } from '@/lib/utils';

/** 'both' shows everything; 'review' shows only In-Review items; 'regular'
 * hides In-Review items (shows pending / in-progress only); 'blocked' shows
 * only Blocked items. */
export type ReviewFilterValue = 'both' | 'review' | 'regular' | 'blocked';

interface ReviewFilterBarProps {
  value: ReviewFilterValue;
  onChange: (value: ReviewFilterValue) => void;
  /** Count of items currently in review, shown on the "In review" chip. */
  reviewCount?: number;
  /** Count of blocked items, shown on the "Blocked" chip. */
  blockedCount?: number;
}

/**
 * Filter a review-mode list by review status. Only rendered when the list has
 * review mode enabled. Composes with the assignee filter on the same page.
 */
export function ReviewFilterBar({
  value,
  onChange,
  reviewCount,
  blockedCount,
}: ReviewFilterBarProps) {
  const chip = (key: ReviewFilterValue, label: string) => (
    <button
      key={key}
      type="button"
      onClick={() => onChange(key)}
      aria-pressed={value === key}
      className={cn(
        'px-2.5 py-1 rounded-full text-xs border transition-colors',
        value === key
          ? 'bg-primary text-primary-foreground border-primary'
          : 'border-border text-muted-foreground hover:bg-accent',
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-0.5 text-xs text-muted-foreground">Review:</span>
      {chip('both', 'All')}
      {chip('review', reviewCount ? `In review (${reviewCount})` : 'In review')}
      {chip('regular', 'Regular')}
      {chip('blocked', blockedCount ? `Blocked (${blockedCount})` : 'Blocked')}
    </div>
  );
}
